import React, { useEffect, useMemo, useState } from "react";
import useClients from "../../Services/ClientServices/useClient";

import {
  Building2,
  User,
  CreditCard,
  Smartphone,
  Mail,
  RotateCcw,
  CheckCircle2,
  ShieldCheck,
  Plus,
  Loader2,
} from "lucide-react";

const THEME_BLUE_BG = "bg-[#f8fbff]";
const BORDER_SMART = "border-gray-300";

const initialForm = {
  accountNumber: "",
  ifscCode: "",
  bankName: "",
  bankHolderName: "",
  mobileNumber: "",
  emailId: "",
  submerchantId: "",
};

export default function ClientBeneficiaryTab({ clientId }) {
  const {
    registerBeneficiaryV1,
    registerBeneficiaryV2, // V2 API
    getUserBankDetails,
    createOrUpdateSubmerchant,
    getSubmerchant,
  } = useClients();

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [beneficiaryLoading, setBeneficiaryLoading] = useState(false);
  const [submerchantLoading, setSubmerchantLoading] = useState(false);
  const [fetchingBankDetails, setFetchingBankDetails] = useState(false);
  const [registeredAccounts, setRegisteredAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(null);

  // Internal tab: Axis Bank V1 | Axis Bank V2
  const [activeTab, setActiveTab] = useState("v1"); // "v1" | "v2"

  const isValidIFSC = (ifsc) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);

  const maskAccountNumber = (value) => {
    const account = String(value || "");
    if (!account) return "—";
    if (account.length <= 4) return account;
    return `${"*".repeat(Math.max(account.length - 4, 0))}${account.slice(-4)}`;
  };

  const mapBankApiToForm = (item = {}) => ({
    accountNumber:
      item.accountNumber ??
      item.account_no ??
      item.us_account ??
      item.account_number ??
      item.beneficiaryAccountNumber ??
      "",
    ifscCode:
      item.ifscCode ??
      item.ifsc ??
      item.ifsc_code ??
      item.beneficiaryIfsc ??
      "",
    bankName: item.bankName ?? item.bank_name ?? item.beneficiaryBankName ?? "",
    bankHolderName:
      item.bankHolderName ??
      item.accountHolderName ??
      item.account_holder_name ??
      item.bank_holder_name ??
      item.beneficiaryName ??
      item.beneficiary_name ??
      "",
    mobileNumber:
      item.mobileNumber ??
      item.mobile ??
      item.phone ??
      item.contactNumber ??
      "",
    emailId: item.emailId ?? item.email ?? item.mailId ?? "",
    submerchantId:
      item.submerchantId ??
      item.submerchant_id ??
      item.easebuzz_submerchant_id ??
      "",
  });

  const activeAccountNumber = useMemo(() => {
    const mapped = mapBankApiToForm(activeAccount || {});
    return mapped.accountNumber || "";
  }, [activeAccount]);

  const validate = () => {
    const e = {};

    if (!form.accountNumber) e.accountNumber = "Account number is required";
    else if (!/^[0-9]{9,18}$/.test(form.accountNumber))
      e.accountNumber = "Invalid format";

    if (!form.ifscCode) e.ifscCode = "IFSC code is required";
    else if (!isValidIFSC(form.ifscCode)) e.ifscCode = "Invalid format";

    if (!form.bankName) e.bankName = "Bank name is required";
    if (!form.bankHolderName) e.bankHolderName = "Holder name is required";

    if (form.mobileNumber && !/^[0-9]{10,15}$/.test(form.mobileNumber)) {
      e.mobileNumber = "Invalid mobile number";
    }

    if (
      form.emailId &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.emailId)
    ) {
      e.emailId = "Invalid email address";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const applyFormData = (item, index = null) => {
    const mapped = mapBankApiToForm(item);

    setForm((prev) => ({
      ...prev,
      ...mapped,
      submerchantId:
        prev.submerchantId ||
        mapped.submerchantId ||
        item?.submerchant_id ||
        item?.submerchantId ||
        "",
    }));

    setSelectedAccountIndex(index);
    setErrors({});
  };

  const resetToDefault = () => {
    if (activeAccount) {
      const mapped = mapBankApiToForm(activeAccount);

      setForm((prev) => ({
        ...mapped,
        submerchantId: prev.submerchantId || mapped.submerchantId || "",
      }));

      setSelectedAccountIndex(null);
      setErrors({});
      return;
    }

    if (registeredAccounts.length > 0) {
      const mapped = mapBankApiToForm(registeredAccounts[0]);

      setForm((prev) => ({
        ...mapped,
        submerchantId: prev.submerchantId || mapped.submerchantId || "",
      }));

      setSelectedAccountIndex(0);
      setErrors({});
      return;
    }

    setForm(initialForm);
    setSelectedAccountIndex(null);
    setErrors({});
  };

  const fetchBankDetails = async () => {
    if (!clientId) return;

    setFetchingBankDetails(true);

    try {
      const [allRes, activeRes, submerchantRes] = await Promise.all([
        getUserBankDetails({ id: clientId, type: "0" }),
        getUserBankDetails({ id: clientId, type: "1" }),
        getSubmerchant({ fk_userid: clientId }),
      ]);

      const allAccounts = Array.isArray(allRes?.response)
        ? allRes.response
        : allRes?.response
          ? [allRes.response]
          : [];

      const active = Array.isArray(activeRes?.response)
        ? activeRes.response[0] || null
        : activeRes?.response || null;

      const submerchantData = Array.isArray(submerchantRes?.response)
        ? submerchantRes.response[0] || null
        : submerchantRes?.response || null;

      const submerchantId = submerchantData?.submerchant_id || "";

      setRegisteredAccounts(allAccounts);
      setActiveAccount(active);

      if (active) {
        setForm({
          ...mapBankApiToForm(active),
          submerchantId,
        });

        const activeIndex = allAccounts.findIndex((acc) => {
          const mapped = mapBankApiToForm(acc);
          return (
            mapped.accountNumber &&
            mapped.accountNumber === mapBankApiToForm(active).accountNumber
          );
        });

        setSelectedAccountIndex(activeIndex >= 0 ? activeIndex : null);
      } else if (allAccounts.length > 0) {
        setForm({
          ...mapBankApiToForm(allAccounts[0]),
          submerchantId,
        });

        setSelectedAccountIndex(0);
      } else {
        setForm({
          ...initialForm,
          submerchantId,
        });

        setSelectedAccountIndex(null);
      }

      setErrors({});
    } catch (error) {
      console.error("fetchBankDetails error:", error);
      setRegisteredAccounts([]);
      setActiveAccount(null);
      setSelectedAccountIndex(null);
      setForm(initialForm);
      setErrors({});
    } finally {
      setFetchingBankDetails(false);
    }
  };

  useEffect(() => {
    fetchBankDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // V1 → registerBeneficiary | V2 → registerBeneficiaryV2
  const submitBeneficiary = async () => {
    if (!validate()) return;

    setBeneficiaryLoading(true);

    try {
      const payload = {
        accountNumber: form.accountNumber,
        ifscCode: form.ifscCode,
        bankName: form.bankName,
        bankHolderName: form.bankHolderName,
        mobileNumber: form.mobileNumber,
        emailId: form.emailId,
      };

      const response =
        activeTab === "v2"
          ? await registerBeneficiaryV2(payload)
          : await registerBeneficiaryV1(payload);

      if (response?.responseCode === "00" || response?.status === true) {
        await fetchBankDetails();
      }
    } finally {
      setBeneficiaryLoading(false);
    }
  };

  const submitSubmerchant = async () => {
    if (!clientId) return;

    setSubmerchantLoading(true);

    try {
      const response = await createOrUpdateSubmerchant({
        fk_userid: clientId,
        submerchant_id: form.submerchantId,
      });

      if (response?.responseCode === "00") {
        await fetchBankDetails();
      }
    } finally {
      setSubmerchantLoading(false);
    }
  };

  return (
    <div className={`w-full ${THEME_BLUE_BG} pb-3`}>
      {activeAccountNumber ? (
        <div className="mb-2 flex justify-end">
          <div className="inline-flex h-7 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5">
            <CheckCircle2 className="text-emerald-600" size={12} />
            <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-700">
              Active Account Linked
            </span>
          </div>
        </div>
      ) : null}

      <main className="mx-0 max-w-[1800px] px-0">
        {/* Internal Tabs: Axis Bank V1 | Axis Bank V2 */}
        <div className="mb-3 flex gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setActiveTab("v1")}
            className={`flex-1 rounded-md px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-all ${
              activeTab === "v1"
                ? "bg-[#01C37D] text-white shadow-sm"
                : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            }`}
          >
            Axis Bank V1
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("v2")}
            className={`flex-1 rounded-md px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-all ${
              activeTab === "v2"
                ? "bg-[#01C37D] text-white shadow-sm"
                : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            }`}
          >
            Axis Bank V2
          </button>
        </div>

        <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-12">
          <div className="space-y-3 lg:col-span-8">
            <Card title="Bank & Settlement Details" icon={Building2}>
              {fetchingBankDetails ? (
                <div className="flex h-[110px] items-center justify-center">
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-[#f8fbff] px-3 py-2">
                    <Loader2
                      className="animate-spin text-[#01C37D]"
                      size={14}
                    />
                    <span className="text-[11px] font-semibold text-gray-600">
                      Fetching bank details...
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Same form for both V1 and V2 */}
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <Field
                      label="Account Number"
                      required
                      error={errors.accountNumber}
                    >
                      <div className="relative">
                        <Input
                          placeholder="9-18 digit account number"
                          value={form.accountNumber}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              accountNumber: e.target.value.replace(/\D/g, ""),
                            }))
                          }
                          hasError={!!errors.accountNumber}
                        />
                        <CreditCard
                          className="absolute right-2.5 top-2 text-gray-400"
                          size={14}
                        />
                      </div>
                    </Field>

                    <Field label="IFSC Code" required error={errors.ifscCode}>
                      <Input
                        placeholder="e.g. UTIB0001234"
                        value={form.ifscCode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            ifscCode: e.target.value.toUpperCase().trim(),
                          }))
                        }
                        hasError={!!errors.ifscCode}
                        className="font-mono uppercase tracking-wider"
                      />
                    </Field>

                    <Field label="Bank Name" required error={errors.bankName}>
                      <Input
                        placeholder="Bank Name"
                        value={form.bankName}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            bankName: e.target.value,
                          }))
                        }
                        hasError={!!errors.bankName}
                      />
                    </Field>

                    <Field
                      label="Account Holder Name"
                      required
                      error={errors.bankHolderName}
                    >
                      <div className="relative">
                        <Input
                          placeholder="As per bank records"
                          value={form.bankHolderName}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              bankHolderName: e.target.value,
                            }))
                          }
                          hasError={!!errors.bankHolderName}
                        />
                        <User
                          className="absolute right-2.5 top-2 text-gray-400"
                          size={14}
                        />
                      </div>
                    </Field>
                  </div>

                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                    <Field label="Mobile Number" error={errors.mobileNumber}>
                      <div className="relative">
                        <Input
                          placeholder="+91"
                          value={form.mobileNumber}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              mobileNumber: e.target.value.replace(
                                /[^\d]/g,
                                "",
                              ),
                            }))
                          }
                          hasError={!!errors.mobileNumber}
                        />
                        <Smartphone
                          className="absolute right-2.5 top-2 text-gray-400"
                          size={14}
                        />
                      </div>
                    </Field>

                    <Field label="Email ID" error={errors.emailId}>
                      <div className="relative">
                        <Input
                          placeholder="billing@company.com"
                          value={form.emailId}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              emailId: e.target.value.trim(),
                            }))
                          }
                          hasError={!!errors.emailId}
                        />
                        <Mail
                          className="absolute right-2.5 top-2 text-gray-400"
                          size={14}
                        />
                      </div>
                    </Field>
                  </div>

                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={submitBeneficiary}
                      disabled={beneficiaryLoading || fetchingBankDetails}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[#01C37D]/20 bg-[#01C37D] px-3 text-[11px] font-bold text-white shadow-sm transition-all hover:bg-[#00ab6d] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {beneficiaryLoading ? (
                        <>
                          <Loader2 size={13} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Plus size={13} />
                          Register Beneficiary
                        </>
                      )}
                    </button>
                  </div>
                  {activeTab == "v1" && (
<>
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                    <Field label="Easebuzz Sub Merchant ID">
                      <Input
                        placeholder="Enter Easebuzz sub merchant id"
                        value={form.submerchantId}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            submerchantId: e.target.value.toUpperCase().trim(),
                          }))
                        }
                      />
                    </Field>

                    <button
                      type="button"
                      onClick={submitSubmerchant}
                      disabled={submerchantLoading || fetchingBankDetails}
                      className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#01C37D]/20 bg-white px-3 text-[11px] font-bold text-[#01C37D] shadow-sm transition-all hover:bg-emerald-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submerchantLoading ? (
                        <>
                          <Loader2 size={13} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <ShieldCheck size={13} />
                          Save Sub Merchant
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mt-2 flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2">
                    <CheckCircle2
                      className="shrink-0 text-[#01C37D]"
                      size={14}
                    />
                    <p className="text-[10px] font-semibold leading-4 text-emerald-800">
                      Registered beneficiaries are eligible for automated fund
                      transfers. Please ensure the IFSC code is correct.
                    </p>
                  </div>

                  <div className="mt-2 flex justify-between border-t border-gray-200 pt-2">
                    <button
                      type="button"
                      onClick={resetToDefault}
                      className="flex h-8 items-center gap-1.5 rounded-md border border-gray-300 px-3 text-[10px] font-bold uppercase tracking-wide text-gray-500 transition-all hover:bg-gray-50 active:scale-95"
                    >
                      <RotateCcw size={12} />
                      Reset
                    </button>
                  </div>
                  </>
                  )}

                </>
              )}
            </Card>
          </div>

          <div className="space-y-3 lg:col-span-4">
            <Card title="Registered Accounts" icon={CreditCard}>
              {fetchingBankDetails ? (
                <div className="py-5 text-center text-[11px] font-semibold text-gray-500">
                  Loading accounts...
                </div>
              ) : registeredAccounts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-[#f8fbff] px-3 py-5 text-center">
                  <p className="text-[11px] font-semibold text-gray-600">
                    No bank accounts found
                  </p>
                  <p className="mt-0.5 text-[10px] text-gray-400">
                    Add a beneficiary to start settlements
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {registeredAccounts.map((account, index) => {
                    const mapped = mapBankApiToForm(account);

                    const isActive =
                      mapped.accountNumber &&
                      activeAccountNumber &&
                      mapped.accountNumber === activeAccountNumber;

                    const isSelected = selectedAccountIndex === index;

                    return (
                      <button
                        key={`${mapped.accountNumber || "acc"}-${index}`}
                        type="button"
                        onClick={() => applyFormData(account, index)}
                        className={`w-full rounded-lg border p-2.5 text-left transition-all ${
                          isSelected || isActive
                            ? "border-[#01C37D] bg-emerald-50/60 shadow-sm"
                            : "border-gray-200 bg-white hover:border-[#01C37D]/40 hover:bg-[#f8fbff]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-[12px] font-bold text-gray-900">
                              {mapped.bankName || "Bank Account"}
                            </div>

                            <div className="mt-0.5 truncate text-[10px] font-semibold text-gray-500">
                              {mapped.bankHolderName || "—"}
                            </div>

                            <div className="mt-1 text-[10px] font-mono text-gray-700">
                              {maskAccountNumber(mapped.accountNumber)}
                            </div>

                            <div className="mt-0.5 text-[9px] uppercase tracking-wide text-gray-400">
                              {mapped.ifscCode || "—"}
                            </div>
                          </div>

                          {isActive ? (
                            <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[8px] font-bold uppercase tracking-wide text-emerald-700">
                              Active
                            </span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className={`rounded-xl border bg-white p-3 shadow-sm ${BORDER_SMART}`}>
      <div className="mb-2.5 flex items-center gap-1.5">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 ${THEME_BLUE_BG} text-[#01C37D]`}
        >
          <Icon size={14} />
        </div>

        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
          {title}
        </div>
      </div>

      <div>{children}</div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-0.5">
      <label
        className={`ml-0.5 text-[10px] font-bold uppercase tracking-tight ${
          error ? "text-rose-500" : "text-gray-500"
        }`}
      >
        {label} {required && <span className="text-rose-500">*</span>}
      </label>

      {children}

      {error ? (
        <p className="ml-0.5 flex items-center gap-1 text-[9px] font-bold uppercase tracking-tight text-rose-500">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Input({ className = "", hasError, ...props }) {
  return (
    <input
      {...props}
      className={`h-8 w-full rounded-md border px-2 text-[11px] font-semibold outline-none transition-all duration-200 ${
        hasError
          ? "border-rose-300 bg-rose-50/50 focus:border-rose-500 focus:ring-1 focus:ring-rose-100"
          : "border-gray-300 bg-white focus:border-[#01C37D] focus:ring-1 focus:ring-[#01C37D]/10"
      } ${className}`}
    />
  );
}